import type { VariantPolicyRow } from "../types";

export const UNASSIGNED_SUPPLIER_KEY = "__unassigned__";

export interface SupplierGroup {
  key: string;
  supplierName: string | null;
  rows: VariantPolicyRow[];
}

/**
 * Group variant rows by supplier, preserving row order within each group.
 * Assigned suppliers are sorted alphabetically (ja); unassigned goes last.
 */
export function groupBySupplier(rows: VariantPolicyRow[]): SupplierGroup[] {
  const byKey = new Map<string, SupplierGroup>();
  for (const row of rows) {
    const key =
      row.supplier_id === null ? UNASSIGNED_SUPPLIER_KEY : String(row.supplier_id);
    const existing = byKey.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      byKey.set(key, { key, supplierName: row.supplier_name, rows: [row] });
    }
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.key === UNASSIGNED_SUPPLIER_KEY) return 1;
    if (b.key === UNASSIGNED_SUPPLIER_KEY) return -1;
    return (a.supplierName ?? "").localeCompare(b.supplierName ?? "", "ja");
  });
}

/**
 * 実効発注点が立っていない＝**アプリが発注要否を判断できていない**行。
 * 「在庫が足りている」と見た目が同じで意味が逆なので、注文書の `is_unmanaged`
 * と同じ述語をここでも一度だけ書く（片方だけ直すと画面ごとに答えが割れる）。
 */
export function isReorderPointUnset(row: VariantPolicyRow): boolean {
  return row.effective_reorder_point <= 0;
}

export interface PolicyRowFilter {
  /** 商品名・SKU の部分一致（大小文字は無視）。空＝絞らない。 */
  query: string;
  /** 発注点が未設定の行だけに絞る（初日の崖＝設定が要る行を数え上げる）。 */
  unsetOnly: boolean;
}

/**
 * 一覧の絞り込み。全商品を1画面に描くと目的の商品に辿り着けないため、
 * サーバーを増やさずに手元で絞る（データは既に全件届いている）。
 *
 * 🔴 名前・SKU が null の行を空文字として扱わない＝検索語があるのに無関係な行が
 * 残ると「検索が効いていない」ように見える。
 */
export function filterPolicyRows(
  rows: VariantPolicyRow[],
  { query, unsetOnly }: PolicyRowFilter,
): VariantPolicyRow[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (unsetOnly && !isReorderPointUnset(row)) return false;
    if (needle === "") return true;
    const haystack = `${row.title ?? ""} ${row.sku ?? ""}`.toLowerCase();
    return haystack.includes(needle);
  });
}

/**
 * Parse a numeric TextField value into a non-negative integer.
 * Empty or invalid input returns undefined (used by bulk-apply, which only
 * acts when a valid value is present).
 */
export function parsePositiveInt(value: string): number | undefined {
  const parsed = parseIntField(value);
  return parsed.kind === "value" ? parsed.value : undefined;
}

export type ParsedField =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "value"; value: number };

/**
 * Parse a numeric TextField value, distinguishing an empty field (→ clear the
 * policy field to null) from invalid input (→ block the save). Used by the
 * per-variant editor so a blanked-out field explicitly clears the value.
 * `min` defaults to 0 (lead time / days of supply); MOQ passes 1, matching
 * the backend's `Field(ge=1)` constraint on `min_order_qty`.
 */
export function parseIntField(value: string, min = 0): ParsedField {
  const trimmed = value.trim();
  if (trimmed === "") return { kind: "empty" };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min) return { kind: "invalid" };
  return { kind: "value", value: n };
}

/**
 * Convert an edited field into the value to send to the upsert API:
 * empty → null (clear), a valid number → that number. Invalid input must be
 * rejected before calling this.
 */
export function toUpsertValue(parsed: ParsedField): number | null {
  return parsed.kind === "value" ? parsed.value : null;
}

/**
 * Safely format a variant's average daily demand for display. The API response
 * is cast without runtime validation, so a freshly-installed store (before its
 * first sync) or a contract drift can surface a missing / non-finite value.
 * Guard it here instead of crashing on `undefined.toFixed`.
 */
export function formatDailyDemand(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(1) : "0.0";
}

/** A variant is below its (auto-calculated) reorder point and needs ordering.
 *
 * 🔴 判定は backend の3面と同じ式にする＝在庫だけでなく **入荷予定と発注済み（未入荷）**
 * も足した「手当済み数量」で見る。`alert_engine` は
 * `covered = available + incoming + open_po <= 発注点`、注文書ドラフトは
 * `発注点 - 在庫 - 入荷予定 - 発注済み <= 0` で同じことを言っている。
 * 在庫だけで判定すると、既に発注済みの商品にこの画面だけが「要発注」と言い続け、
 * 二重発注へ誘導する。
 *
 * `incoming` / `ordered_open` が欠けるのは SPA が backend より先に出た
 * デプロイスキューの間だけ＝0 として扱い、従来どおり在庫だけで判定する。
 */
export function needsReorder(row: VariantPolicyRow): boolean {
  const covered = row.available + (row.incoming ?? 0) + (row.ordered_open ?? 0);
  return row.effective_reorder_point > 0 && covered <= row.effective_reorder_point;
}
